import { useTranslation } from 'react-i18next'
import { Alert, Button, CircularProgress, MenuItem, Stack, TextField, Typography } from '@mui/material'

import { SerialPortsState } from '../../model/useSerialPorts'

export type SerialPortPickerProps = {
  ports: SerialPortsState
  value: string | undefined
  onChange: (port: string) => void
}

const MANUAL_PORT_OPTION = '__manual__'

/** Pick the serial port the device is plugged into, with a manual fallback. */
export default function SerialPortPicker({ ports, value, onChange }: SerialPortPickerProps) {
  const { t } = useTranslation()
  const isKnownPort = !!value && ports.ports.some((port) => port.path === value)
  const selectValue = !value ? '' : isKnownPort ? value : MANUAL_PORT_OPTION

  return (
    <Stack spacing={1.5}>
      <Alert severity="info">{t('page.configuration.MedconnectDevice.wizard.serial.hint')}</Alert>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          select
          fullWidth
          size="small"
          label={t('page.configuration.MedconnectDevice.wizard.serial.port')}
          value={selectValue}
          onChange={(event) => onChange(event.target.value === MANUAL_PORT_OPTION ? '' : event.target.value)}
        >
          {ports.ports.map((port) => (
            <MenuItem key={port.path} value={port.path}>
              {port.path}{port.friendlyName || port.manufacturer ? ` · ${port.friendlyName ?? port.manufacturer}` : ''}
            </MenuItem>
          ))}
          <MenuItem value={MANUAL_PORT_OPTION}>{t('page.configuration.MedconnectDevice.wizard.serial.manual')}</MenuItem>
        </TextField>
        {ports.isLoading ? <CircularProgress size={20} /> : <Button size="small" onClick={ports.refresh}>{t('page.configuration.MedconnectDevice.wizard.serial.refresh')}</Button>}
      </Stack>
      {!ports.isLoading && ports.ports.length === 0 && (
        <Typography variant="body2" color="text.secondary">{t('page.configuration.MedconnectDevice.wizard.serial.noneFound')}</Typography>
      )}
      {selectValue === MANUAL_PORT_OPTION || (!isKnownPort && !!value) ? (
        <TextField size="small" fullWidth label={t('page.configuration.MedconnectDevice.wizard.serial.manualPort')} placeholder="COM3" value={value ?? ''} onChange={(event) => onChange(event.target.value)} />
      ) : null}
    </Stack>
  )
}
